/**
 * One Thread — the chat is the only surface.
 *
 * Header carries a one-line brief (tap → "Your brief" sheet). The board
 * became a summonable sheet: bottom sheet on phones, right dock on
 * desktop. Deep material (pack, compare, visit day) opens the sheet and
 * leaves a receipt in the thread when it closes. Exactly one chip rail,
 * on the composer (rendered by ChatColumn).
 */

import { useEffect, useRef, useState } from 'react';
import { useAdvisor, formatPrefsSummary } from '@/lib/advisor-context';
import { useBuyerAuth } from '@/lib/buyer-auth-context';
import { ChatColumn } from '@/components/prototype/chat-column';
import {
  BoardCompare,
  BoardFocusHub,
  BoardProject,
  BoardVisit,
  useEarnedLanes,
} from '@/components/prototype/board-column';
import type { PrefKey } from '@/lib/types';

export function OneThread() {
  const { boardSheetOpen, setBoardSheetOpen } = useAdvisor();
  const [briefOpen, setBriefOpen] = useState(false);
  // The header board button always lands on the deck — even when the last
  // summoned view was a pack/compare/visit. Chat-driven opens clear it.
  const [deckForce, setDeckForce] = useState(false);
  const [openDeckNonce, setOpenDeckNonce] = useState(0);

  const onBoardButton = () => {
    if (boardSheetOpen) {
      setBoardSheetOpen(false);
      return;
    }
    setDeckForce(true);
    setOpenDeckNonce((n) => n + 1);
    setBoardSheetOpen(true);
  };

  return (
    <div className={`ot-shell${boardSheetOpen || briefOpen ? ' ot-sheet-is-open' : ''}`}>
      <OneThreadHeader onOpenBrief={() => setBriefOpen(true)} onBoardButton={onBoardButton} />
      <div className="ot-main">
        <ChatColumn />
      </div>
      <BoardSheet
        deckForce={deckForce}
        setDeckForce={setDeckForce}
        openDeckNonce={openDeckNonce}
      />
      <BriefSheet open={briefOpen} onClose={() => setBriefOpen(false)} />
    </div>
  );
}

/** Short header form of the brief: "For us · North · ≤ ₹70 L". */
function briefLineParts(
  prefs: ReturnType<typeof useAdvisor>['prefs'],
  snapshot: Record<string, string | undefined> | null,
): string[] {
  const rawPurpose = snapshot?.purpose ?? prefs.purpose ?? '';
  const purpose = /invest/i.test(rawPurpose)
    ? 'Investment'
    : rawPurpose
      ? 'For us'
      : '';
  const area = snapshot?.location ?? prefs.location ?? '';
  // Prefer the SPA's own budget BAND (stable — "₹70L–1 Cr") over the server
  // snapshot, whose budget is the parsed cap and flips to "₹1 Cr" after the
  // search turn (Cowork F5: the header label mutated once results landed).
  const budget = prefs.budget ?? snapshot?.budget ?? '';
  return [purpose, area, budget].filter(Boolean);
}

function OneThreadHeader({
  onOpenBrief,
  onBoardButton,
}: {
  onOpenBrief: () => void;
  onBoardButton: () => void;
}) {
  const { prefs, signalSnapshot, goHome } = useAdvisor();
  const lanes = useEarnedLanes();
  const earned = lanes.filter((l) => l.on).length;
  const parts = briefLineParts(prefs, signalSnapshot);

  return (
    <header className="ot-hd">
      <button type="button" className="ot-brand" onClick={goHome} title="Naya — back to the landing">
        Naya
      </button>
      <button
        type="button"
        className="ot-brief-line"
        onClick={onOpenBrief}
        title="Your brief — tap to review or change"
      >
        <span className="ot-brief-text">
          {parts.length > 0 ? (
            parts.map((p, i) => (
              <span key={p}>
                {i > 0 && <span className="ot-brief-dot"> · </span>}
                <b>{p}</b>
              </span>
            ))
          ) : (
            'Anonymous · no brief yet'
          )}
        </span>
        <span className="ot-brief-edit" aria-hidden="true">
          ✎
        </span>
      </button>
      <button
        type="button"
        className="ot-board-btn"
        aria-label="Your board"
        onClick={onBoardButton}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        </svg>
        {earned > 0 && <span className="ot-board-badge">{earned}</span>}
      </button>
    </header>
  );
}

// ── Board sheet ──────────────────────────────────────────────────────

function BoardSheet({
  deckForce,
  setDeckForce,
  openDeckNonce,
}: {
  deckForce: boolean;
  setDeckForce: (v: boolean) => void;
  openDeckNonce: number;
}) {
  const {
    board,
    boardSheetOpen,
    setBoardSheetOpen,
    summonMatchesNonce,
    focusedProject,
    compareMatrix,
    visitRouteStopIds,
    matchProjects,
    addReceipt,
    sendText,
    getBoardProject,
  } = useAdvisor();
  const [deckMode, setDeckMode] = useState<'deck' | 'matches'>('deck');
  const prevViewRef = useRef(board.view);
  const prevProjectRef = useRef(board.projectId);
  const lastSummonMatchesRef = useRef(0);
  const lastOpenDeckRef = useRef(0);

  // Summoned by the chat: a turn that lands on a pack, compare or
  // visit day slides the sheet up. Recommend matches are summoned separately
  // (A4 — no in-thread card stack).
  useEffect(() => {
    const prevView = prevViewRef.current;
    const prevProject = prevProjectRef.current;
    prevViewRef.current = board.view;
    prevProjectRef.current = board.projectId;
    const deepView = board.view === 'project' || board.view === 'compare' || board.view === 'visit';
    if (!deepView) return;
    if (board.view !== prevView || (board.view === 'project' && board.projectId !== prevProject)) {
      setDeckForce(false);
      setBoardSheetOpen(true);
    }
  }, [board.view, board.projectId, setBoardSheetOpen, setDeckForce]);

  // Header board button → earned deck (not the last matches list).
  useEffect(() => {
    if (!openDeckNonce || openDeckNonce === lastOpenDeckRef.current) return;
    lastOpenDeckRef.current = openDeckNonce;
    setDeckMode('deck');
  }, [openDeckNonce]);

  // Recommend publish → open sheet on the match cards (not the earned deck).
  useEffect(() => {
    if (!summonMatchesNonce || summonMatchesNonce === lastSummonMatchesRef.current) return;
    lastSummonMatchesRef.current = summonMatchesNonce;
    setDeckForce(true);
    setDeckMode('matches');
    setBoardSheetOpen(true);
  }, [summonMatchesNonce, setBoardSheetOpen, setDeckForce]);

  const close = () => {
    // Leaving a summoned surface leaves a receipt — thread memory.
    if (board.view === 'project' && board.projectId) {
      const name = getBoardProject(board.projectId).name;
      addReceipt({
        kind: 'project',
        label: `${name} pack`,
        sub: 'read — on your board',
        projectId: board.projectId,
      });
    } else if (board.view === 'compare' && (compareMatrix?.projects.length ?? 0) >= 2) {
      addReceipt({ kind: 'compare', label: 'Side-by-side compare', sub: 'on your board' });
    } else if (board.view === 'visit' && visitRouteStopIds.length > 0) {
      addReceipt({
        kind: 'visit',
        label: 'Visit day',
        sub: `${visitRouteStopIds.length} stop${visitRouteStopIds.length === 1 ? '' : 's'} — on your board`,
      });
    }
    setBoardSheetOpen(false);
    setDeckForce(false);
  };

  const showDeck =
    deckForce || (board.view !== 'project' && board.view !== 'compare' && board.view !== 'visit');
  const title = !showDeck
    ? board.view === 'project'
      ? focusedProject?.name ?? 'Project pack'
      : board.view === 'compare'
        ? 'Side by side'
        : 'Your visit day'
    : deckMode === 'matches'
      ? 'Your matches'
      : 'Your board';
  const lanes = useEarnedLanes();
  const earned = lanes.filter((l) => l.on).length;
  const sub = !showDeck
    ? board.view === 'project' && focusedProject
      ? `${focusedProject.builder} · ${focusedProject.location}`
      : ''
    : earned > 0
      ? `${earned} earned in this chat`
      : 'Nothing earned yet — it fills as we talk';

  return (
    <>
      <div
        className={`ot-scrim${boardSheetOpen ? ' ot-scrim-on' : ''}`}
        onClick={close}
        aria-hidden="true"
      />
      <div
        className={`ot-sheet${boardSheetOpen ? ' ot-sheet-on' : ''}`}
        role="dialog"
        aria-label="Your board"
      >
        <div className="ot-sheet-hd">
          <div className="ot-grab" />
          <h3>{title}</h3>
          {sub && <div className="ot-sheet-sub">{sub}</div>}
          <button type="button" className="ot-sheet-x" onClick={close} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="ot-sheet-body na-scroll">
          {showDeck ? (
            deckMode === 'matches' ? (
              <>
                <button type="button" className="na-back-link" onClick={() => setDeckMode('deck')}>
                  ← your board
                </button>
                <BoardFocusHub />
              </>
            ) : (
              <BoardDeck
                onOpenMatches={() => setDeckMode('matches')}
                onNavigate={() => setDeckForce(false)}
              />
            )
          ) : board.view === 'project' ? (
            <BoardProject />
          ) : board.view === 'compare' ? (
            <BoardCompare />
          ) : (
            <BoardVisit />
          )}
        </div>
        {board.view === 'project' && focusedProject && (
          <div className="ot-sheet-ft">
            <button
              type="button"
              className="ot-btn-dark"
              onClick={() => sendText(`Plan a visit to ${focusedProject.name}`)}
            >
              Add to visit day
            </button>
            <button type="button" className="ot-btn-line" onClick={close}>
              Done reading
            </button>
          </div>
        )}
      </div>
    </>
  );
}

/** The deck — earned artifacts open, unearned stay ghost rows. Same
 *  truth-bound lanes as the earned roadmap; a lane is tappable only when
 *  the session actually produced it. */
function BoardDeck({
  onOpenMatches,
  onNavigate,
}: {
  onOpenMatches: () => void;
  onNavigate: () => void;
}) {
  const {
    openBoardProject,
    setBoardView,
    engagedProjects,
    compareMatrix,
    matchProjects,
  } = useAdvisor();
  const { buyer, openSignIn, openProfile } = useBuyerAuth();
  const lanes = useEarnedLanes();

  const firstEngaged = engagedProjects[0]?.id;
  const go = (fn: () => void) => () => {
    onNavigate();
    fn();
  };
  const actions: Record<string, (() => void) | undefined> = {
    '01': matchProjects.length > 0 ? onOpenMatches : undefined,
    '02': firstEngaged ? go(() => openBoardProject(firstEngaged, 'units')) : undefined,
    '03': firstEngaged ? go(() => openBoardProject(firstEngaged, 'legal')) : undefined,
    '04': firstEngaged ? go(() => openBoardProject(firstEngaged, 'price')) : undefined,
    '05': firstEngaged ? go(() => openBoardProject(firstEngaged, 'emi')) : undefined,
    '06': go(() => setBoardView('visit')),
  };
  const ghostHints: Record<string, string> = {
    '01': 'finish the four questions',
    '02': 'open any match',
    '03': 'open any match',
    '04': 'ask about price',
    '05': 'run the EMI numbers',
    '06': 'add a project you trust',
  };

  const on = lanes.filter((l) => l.on);
  const off = lanes.filter((l) => !l.on);

  return (
    <>
      {on.map((l) => {
        const act = actions[l.n];
        return (
          <button
            key={l.n}
            type="button"
            className="ot-deck-row"
            disabled={!act}
            onClick={act}
          >
            <span className="ot-deck-n">✓</span>
            <span className="ot-deck-body">
              <span className="ot-deck-t">{l.title}</span>
              <span className="ot-deck-s">{l.note || l.desc}</span>
            </span>
            <span className="ot-deck-go">›</span>
          </button>
        );
      })}
      {(compareMatrix?.projects.length ?? 0) >= 2 && (
        <button type="button" className="ot-deck-row" onClick={go(() => setBoardView('compare'))}>
          <span className="ot-deck-n">✓</span>
          <span className="ot-deck-body">
            <span className="ot-deck-t">Side-by-side compare</span>
            <span className="ot-deck-s">
              {compareMatrix!.projects.length} projects · {compareMatrix!.rows.length} rows
            </span>
          </span>
          <span className="ot-deck-go">›</span>
        </button>
      )}
      {off.length > 0 && <div className="ot-deck-kicker">Still to earn</div>}
      {off.map((l) => (
        <div key={l.n} className="ot-deck-row ot-deck-ghost">
          <span className="ot-deck-n">·</span>
          <span className="ot-deck-body">
            <span className="ot-deck-t">{l.title}</span>
            <span className="ot-deck-s">{ghostHints[l.n] ?? l.desc}</span>
          </span>
        </div>
      ))}
      <div className="ot-deck-kicker">Your profile</div>
      {buyer ? (
        <button type="button" className="ot-deck-row" onClick={openProfile}>
          <span className="ot-deck-n ot-deck-n-key">✓</span>
          <span className="ot-deck-body">
            <span className="ot-deck-t">{buyer.masked}</span>
            <span className="ot-deck-s">Signed in — board follows you · privacy ledger</span>
          </span>
          <span className="ot-deck-go">›</span>
        </button>
      ) : (
        <button type="button" className="ot-deck-row ot-deck-ghost" onClick={openSignIn}>
          <span className="ot-deck-n">·</span>
          <span className="ot-deck-body">
            <span className="ot-deck-t">Keep your search</span>
            <span className="ot-deck-s">One OTP — never for calls, never shared</span>
          </span>
          <span className="ot-deck-go">›</span>
        </button>
      )}
    </>
  );
}

// ── Brief sheet ──────────────────────────────────────────────────────

const BRIEF_ROWS: Array<{
  key: PrefKey;
  label: string;
  group: 'basics' | 'instincts';
  refineHint: 'area' | 'budget' | 'general';
}> = [
  { key: 'purpose', label: 'Here for', group: 'basics', refineHint: 'general' },
  { key: 'budget', label: 'Budget', group: 'basics', refineHint: 'budget' },
  { key: 'location', label: 'Side of city', group: 'basics', refineHint: 'area' },
  { key: 'propertyType', label: 'Type of home', group: 'basics', refineHint: 'general' },
  { key: 'bhk', label: 'Configuration', group: 'basics', refineHint: 'general' },
  { key: 'worries', label: 'Quiet worry', group: 'instincts', refineHint: 'general' },
];

function BriefSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { prefs, signalSnapshot, briefPhase, editBriefAnswer, beginBriefRefine, resetSession } =
    useAdvisor();

  const valueFor = (key: PrefKey): string => {
    switch (key) {
      case 'purpose':
        return signalSnapshot?.purpose
          ? /invest/i.test(signalSnapshot.purpose)
            ? 'Investment'
            : 'For us'
          : prefs.purpose ?? '';
      case 'budget':
        return signalSnapshot?.budget ?? prefs.budget ?? '';
      case 'location':
        return signalSnapshot?.location ?? prefs.location ?? '';
      case 'propertyType':
        return prefs.propertyTypes.join(' · ');
      case 'bhk':
        return [...prefs.bhkSelections, ...prefs.plotSizeSelections].join(' · ');
      case 'worries':
        return prefs.worries.filter((w) => w !== 'Nothing specific').join(' · ');
      default:
        return '';
    }
  };

  const onEdit = (key: PrefKey, refineHint: 'area' | 'budget' | 'general') => {
    onClose();
    if (briefPhase === 'collecting') {
      editBriefAnswer(key);
    } else {
      beginBriefRefine(refineHint);
    }
  };

  const groups: Array<['basics' | 'instincts', string]> = [
    ['basics', 'Basics'],
    ['instincts', 'Instincts'],
  ];

  return (
    <>
      <div className={`ot-scrim${open ? ' ot-scrim-on' : ''}`} onClick={onClose} aria-hidden="true" />
      <div className={`ot-sheet${open ? ' ot-sheet-on' : ''}`} role="dialog" aria-label="Your brief">
        <div className="ot-sheet-hd">
          <div className="ot-grab" />
          <h3>Your brief</h3>
          <div className="ot-sheet-sub">Everything you&apos;ve told me — tap a line to change it</div>
          <button type="button" className="ot-sheet-x" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="ot-sheet-body na-scroll">
          {groups.map(([group, title]) => (
            <div key={group} className="ot-brief-group">
              <div className="ot-deck-kicker">{title}</div>
              {BRIEF_ROWS.filter((r) => r.group === group).map((r) => {
                const v = valueFor(r.key);
                return v ? (
                  <button
                    key={r.key}
                    type="button"
                    className="ot-brief-row"
                    onClick={() => onEdit(r.key, r.refineHint)}
                  >
                    <span>
                      <span className="au-k">{r.label}</span>
                      <span className="au-v">{v}</span>
                    </span>
                    <span className="ot-brief-pen">✎</span>
                  </button>
                ) : (
                  <div key={r.key} className="ot-brief-row ot-brief-ghost">
                    <span>
                      <span className="au-k">{r.label}</span>
                      <span className="au-v">
                        {r.key === 'worries'
                          ? 'nothing flagged yet'
                          : r.key === 'bhk' || r.key === 'propertyType'
                            ? 'comes up when it matters'
                            : 'not asked yet'}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div className="ot-sheet-ft">
          <div className="ot-brief-foot-note">
            Nothing here reaches a builder until you choose a visit.
          </div>
          <button
            type="button"
            className="ot-btn-line"
            onClick={() => {
              onClose();
              resetSession();
            }}
          >
            Start over — new anonymous line
          </button>
        </div>
      </div>
    </>
  );
}

export { formatPrefsSummary };
